import { useState, useEffect } from 'react';
import { Link, useNavigate, useLocation } from 'react-router-dom';
import PropTypes from 'prop-types';
import useAuthStore from '@/stores/authStore';
import { Button } from '@components';

const Navigation = () => {
  const { user, logout } = useAuthStore();
  const navigate = useNavigate();
  const location = useLocation();
  const [isMenuOpen, setIsMenuOpen] = useState(false);
  const [isUserMenuOpen, setIsUserMenuOpen] = useState(false);

  // Close mobile menu on route change
  useEffect(() => {
    setIsMenuOpen(false);
  }, [location.pathname]);

  // Close user menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (!e.target.closest('.user-menu-container')) {
        setIsUserMenuOpen(false);
      }
    };

    if (isUserMenuOpen) {
      document.addEventListener('click', handleClickOutside);
      return () => document.removeEventListener('click', handleClickOutside);
    }
  }, [isUserMenuOpen]);

  const handleLogout = async () => {
    try {
      await logout();
      navigate('/login');
    } catch (error) {
      console.error('Logout error:', error);
    }
  };

  const handleNavClick = (e, path) => {
    // Handle hash navigation for same-page sections
    if (path.startsWith('/#')) {
      e.preventDefault();
      const sectionId = path.substring(2); // Remove '/#'
      const element = document.getElementById(sectionId);
      
      if (location.pathname === '/') {
        // Already on home page, just scroll
        element?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      } else {
        // Navigate to home first, then scroll
        navigate('/');
        setTimeout(() => {
          const el = document.getElementById(sectionId);
          el?.scrollIntoView({ behavior: 'smooth', block: 'start' });
        }, 100);
      }
    }
  };

  const isAdmin = user?.role === 'admin';

  const marketingDark =
    !isAdmin &&
    (location.pathname === '/' ||
      location.pathname.startsWith('/blogs') ||
      location.pathname === '/news' ||
      location.pathname === '/privacy');

  const hashFromPath = (path) => {
    if (!path.startsWith('/#')) return null;
    return `#${path.slice(2)}`;
  };

  const navLinks = isAdmin
    ? [
        { name: 'Home', path: '/' },
        { name: 'Projects', path: '/projects' },
        { name: 'Posts', path: '/posts' },
        { name: 'Blogs', path: '/blogs' },
        { name: 'News', path: '/news' },
        { name: 'Analytics', path: '/analytics' },
        { name: 'Calendar', path: '/calendar' },
        { name: 'Admin', path: '/admin' },
      ]
    : [
        { name: 'Services', path: '/#services' },
        { name: 'Solutions', path: '/#solutions' },
        { name: 'About', path: '/#about' },
        { name: 'News', path: '/news' },
        { name: 'Blogs', path: '/blogs' },
        { name: 'Work with me', path: '/work-with-me' },
      ];

  const linkIsActive = (link) => {
    const h = hashFromPath(link.path);
    if (h) {
      return location.pathname === '/' && location.hash === h;
    }
    if (link.path === '/blogs') {
      return location.pathname.startsWith('/blogs');
    }
    return location.pathname === link.path;
  };

  return (
    <nav
      className={`sticky top-0 z-50 border-b transition-colors ${
        marketingDark
          ? 'bg-primary-navy/95 backdrop-blur-md border-white/20 shadow-none'
          : 'bg-white shadow-nav border-transparent'
      }`}
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex justify-between items-center h-16">
          {/* Logo */}
          <Link to="/" className="flex items-center gap-2">
            <img
              src="/byamba.jpg"
              alt="Byamba Enkhbat"
              className="w-10 h-10 rounded-full object-cover ring-2 ring-primary-teal/50"
            />
            <span
              className={`text-h5 font-bold ${marketingDark ? 'text-white' : 'text-primary-navy'}`}
            >
              Byamba Enkhbat
            </span>
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center gap-8">
            {navLinks.map((link) => (
              link.name === 'Admin' ? (
                <Link
                  key={link.path}
                  to={link.path}
                  className="text-xs font-semibold px-3 py-1.5 rounded-full bg-purple-100 text-purple-700 hover:bg-purple-200 transition-colors"
                >
                  ⚙ Admin
                </Link>
              ) : (
                <Link
                  key={link.path}
                  to={link.path}
                  onClick={(e) => handleNavClick(e, link.path)}
                  className={`text-sm font-medium transition-colors ${
                    linkIsActive(link)
                      ? 'text-primary-teal'
                      : marketingDark
                        ? 'text-slate-300 hover:text-primary-teal'
                        : 'text-neutral-charcoal hover:text-primary-teal'
                  }`}
                >
                  {link.name}
                </Link>
              )
            ))}
          </div>

          {/* Right Side */}
          <div className="hidden md:flex items-center gap-4">
            {user ? (
              // User Menu
              <div className="relative user-menu-container">
                <button
                  onClick={() => setIsUserMenuOpen(!isUserMenuOpen)}
                  className="flex items-center gap-2 p-2 rounded-lg hover:bg-neutral-soft-gray transition-colors"
                  aria-haspopup="menu"
                  aria-expanded={isUserMenuOpen}
                  aria-label="Account menu"
                >
                  <div className="w-8 h-8 rounded-full bg-primary-navy flex items-center justify-center text-white font-semibold text-sm">
                    {user.displayName?.charAt(0).toUpperCase() || 'U'}
                  </div>
                  <span className="text-sm font-medium text-neutral-charcoal">
                    {user.displayName || 'User'}
                  </span>
                  <svg
                    className={`w-4 h-4 text-neutral-medium-gray transition-transform ${
                      isUserMenuOpen ? 'rotate-180' : ''
                    }`}
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M19 9l-7 7-7-7"
                    />
                  </svg>
                </button>

                {/* Dropdown Menu */}
                {isUserMenuOpen && (
                  <div className="absolute right-0 mt-2 w-56 bg-white rounded-xl shadow-dropdown border border-neutral-soft-gray py-2">
                    <div className="px-4 py-3 border-b border-neutral-soft-gray">
                      <p className="text-sm font-medium text-neutral-charcoal">
                        {user.displayName || 'User'}
                      </p>
                      <p className="text-xs text-neutral-medium-gray truncate">
                        {user.email}
                      </p>
                    </div>

                    <Link
                      to="/profile"
                      className="flex items-center gap-3 px-4 py-2 text-sm text-neutral-charcoal hover:bg-neutral-soft-gray transition-colors"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                        />
                      </svg>
                      Profile
                    </Link>

                    <Link
                      to="/settings"
                      className="flex items-center gap-3 px-4 py-2 text-sm text-neutral-charcoal hover:bg-neutral-soft-gray transition-colors"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                        />
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                        />
                      </svg>
                      Settings
                    </Link>

                    <div className="border-t border-neutral-soft-gray my-2" />

                    <button
                      onClick={handleLogout}
                      className="flex items-center gap-3 px-4 py-2 text-sm text-action-error-red hover:bg-red-50 transition-colors w-full"
                    >
                      <svg
                        className="w-5 h-5"
                        fill="none"
                        stroke="currentColor"
                        viewBox="0 0 24 24"
                      >
                        <path
                          strokeLinecap="round"
                          strokeLinejoin="round"
                          strokeWidth={2}
                          d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                        />
                      </svg>
                      Logout
                    </button>
                  </div>
                )}
              </div>
            ) : marketingDark ? (
              <>
                <Link
                  to="/login"
                  className="text-sm font-medium text-slate-300 hover:text-white transition-colors"
                >
                  Login
                </Link>
                <Link to="/work-with-me">
                  <span className="inline-flex items-center justify-center rounded-full bg-gradient-to-r from-primary-teal to-emerald-500 px-5 py-2 text-sm font-semibold text-white shadow-lg shadow-primary-teal/25 hover:opacity-95 transition-opacity">
                    Work with me
                  </span>
                </Link>
              </>
            ) : (
              <>
                <Link to="/login">
                  <Button variant="outline" size="sm">
                    Login
                  </Button>
                </Link>
                <Link to="/work-with-me">
                  <Button variant="primary" size="sm">
                    Work with me
                  </Button>
                </Link>
              </>
            )}
          </div>

          {/* Mobile Menu Button */}
          <button
            className={`md:hidden p-2 rounded-lg transition-colors ${
              marketingDark ? 'hover:bg-white/10' : 'hover:bg-neutral-soft-gray'
            }`}
            onClick={() => setIsMenuOpen(!isMenuOpen)}
            aria-label={isMenuOpen ? 'Close menu' : 'Open menu'}
            aria-expanded={isMenuOpen}
            aria-controls="mobile-menu"
          >
            <svg
              className={`w-6 h-6 ${marketingDark ? 'text-white' : 'text-neutral-charcoal'}`}
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              aria-hidden="true"
            >
              {isMenuOpen ? (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M6 18L18 6M6 6l12 12"
                />
              ) : (
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M4 6h16M4 12h16M4 18h16"
                />
              )}
            </svg>
          </button>
        </div>

        {/* Mobile Menu */}
        {isMenuOpen && (
          <div
            id="mobile-menu"
            className={`md:hidden py-4 border-t ${
              marketingDark ? 'border-white/10' : 'border-neutral-soft-gray'
            }`}
          >
            <div className="space-y-2">
              <Link
                to="/"
                onClick={() => setIsMenuOpen(false)}
                className={`block px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                  location.pathname === '/' && !location.hash
                    ? 'bg-primary-teal/15 text-primary-teal'
                    : marketingDark
                      ? 'text-slate-200 hover:bg-white/10'
                      : 'text-neutral-charcoal hover:bg-neutral-soft-gray'
                }`}
              >
                Home
              </Link>
              {navLinks.map((link) => (
                link.name === 'Admin' ? (
                  <Link
                    key={link.path}
                    to={link.path}
                    className="block px-4 py-2 rounded-lg text-sm font-semibold bg-purple-50 text-purple-700"
                  >
                    ⚙ Admin Panel
                  </Link>
                ) : (
                  <Link
                    key={link.path}
                    to={link.path}
                    onClick={(e) => handleNavClick(e, link.path)}
                    className={`block px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
                      linkIsActive(link)
                        ? 'bg-primary-teal/15 text-primary-teal'
                        : marketingDark
                          ? 'text-slate-200 hover:bg-white/10'
                          : 'text-neutral-charcoal hover:bg-neutral-soft-gray'
                    }`}
                  >
                    {link.name}
                  </Link>
                )
              ))}
            </div>

            {user ? (
              <div className="mt-4 pt-4 border-t border-neutral-soft-gray space-y-2">
                <Link
                  to="/profile"
                  className="flex items-center gap-3 px-4 py-2 rounded-lg text-sm text-neutral-charcoal hover:bg-neutral-soft-gray transition-colors"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M16 7a4 4 0 11-8 0 4 4 0 018 0zM12 14a7 7 0 00-7 7h14a7 7 0 00-7-7z"
                    />
                  </svg>
                  Profile
                </Link>
                <Link
                  to="/settings"
                  className="flex items-center gap-3 px-4 py-2 rounded-lg text-sm text-neutral-charcoal hover:bg-neutral-soft-gray transition-colors"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z"
                    />
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M15 12a3 3 0 11-6 0 3 3 0 016 0z"
                    />
                  </svg>
                  Settings
                </Link>
                <button
                  onClick={handleLogout}
                  className="flex items-center gap-3 px-4 py-2 rounded-lg text-sm text-action-error-red hover:bg-red-50 transition-colors w-full"
                >
                  <svg
                    className="w-5 h-5"
                    fill="none"
                    stroke="currentColor"
                    viewBox="0 0 24 24"
                  >
                    <path
                      strokeLinecap="round"
                      strokeLinejoin="round"
                      strokeWidth={2}
                      d="M17 16l4-4m0 0l-4-4m4 4H7m6 4v1a3 3 0 01-3 3H6a3 3 0 01-3-3V7a3 3 0 013-3h4a3 3 0 013 3v1"
                    />
                  </svg>
                  Logout
                </button>
              </div>
            ) : (
              <div
                className={`mt-4 pt-4 border-t space-y-2 ${
                  marketingDark ? 'border-white/10' : 'border-neutral-soft-gray'
                }`}
              >
                {marketingDark ? (
                  <>
                    <Link
                      to="/login"
                      onClick={() => setIsMenuOpen(false)}
                      className="block w-full text-center py-2 text-sm font-medium text-slate-300 hover:text-white"
                    >
                      Login
                    </Link>
                    <Link
                      to="/work-with-me"
                      onClick={() => setIsMenuOpen(false)}
                      className="block w-full text-center rounded-full bg-gradient-to-r from-primary-teal to-emerald-500 py-3 text-sm font-semibold text-white"
                    >
                      Work with me
                    </Link>
                  </>
                ) : (
                  <>
                    <Link to="/login" className="block" onClick={() => setIsMenuOpen(false)}>
                      <Button variant="ghost" className="w-full">
                        Login
                      </Button>
                    </Link>
                    <Link to="/work-with-me" className="block" onClick={() => setIsMenuOpen(false)}>
                      <Button variant="primary" className="w-full">
                        Work with me
                      </Button>
                    </Link>
                  </>
                )}
              </div>
            )}
          </div>
        )}
      </div>
    </nav>
  );
};

Navigation.propTypes = {};

export default Navigation;
